import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'
import Pagination from 'react-js-pagination'
import { useBlogPostInfiniteQuery } from '../../../modules/apis/student/blog'
import LinkLocale from '../../../modules/i18n/LinkLocale'
import { s_blog } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import client from '../../../modules/sanityClient'
import {
  ICON_CHEVRON_LEFT_BLACK,
  ICON_CHEVRON_RIGHT_BLACK,
  ICON_PAGINATION_LEFT,
  ICON_PAGINATION_RIGHT,
} from '../../../modules/svg/svg'
import urls from '../../../modules/urls'
import { BLOG_NOTICE_TAB_QUERY } from '../../../sanity/groqQueries'
import { BLOG_NOTICE_TAB_QUERYResult } from '../../../sanity/types'
import LoadingSpinner from '../../common/LoadingSpinner'
import BlogCard from './BlogCard'
import FAQSection from './FAQSection'
import { IBlogComponent, IBlogPostListSection, MENU_ID } from './type'

// Fetch function for posts
const fetchPosts = async (lang, form) => {
  const params = {
    lang: lang,
    form: form,
  }

  const posts: BLOG_NOTICE_TAB_QUERYResult[] = await client.fetch(BLOG_NOTICE_TAB_QUERY, params)

  return posts
}

const NoticeComponent = ({ isMobile }) => {
  const router = useRouter()
  const { data: posts, isLoading }: any = useQuery({
    queryKey: ['posts', router.query.lang, MENU_ID.NOTICE],
    queryFn: () => fetchPosts(router.query.lang, MENU_ID.NOTICE),
  })
  const [activePage, setActivePage] = useState(1)
  const itemsPerPage = 10

  const startIndex = (activePage - 1) * itemsPerPage
  const endIndex = activePage * itemsPerPage
  const displayedPosts = posts?.slice(startIndex, endIndex)

  return (
    <>
      <div className={style(isMobile).noticeLayout}>
        {isLoading ? (
          <div className="flex h-[400px] w-full items-center justify-center">
            <LoadingSpinner show primary lg padded />
          </div>
        ) : (
          displayedPosts?.map((postItem, i) => {
            return (
              <LinkLocale href={urls.student.landing.blog.detail(postItem.slug.current)} key={i}>
                <div className={style(isMobile).noticeElement}>
                  <div className="Body1-16Md">{postItem.title}</div>
                  <div className="Body2-14Md text-gray-400">{postItem.publishedAt}</div>
                </div>
              </LinkLocale>
            )
          })
        )}

        <div className="Normal-Pagination mt-[40px]">
          <Pagination
            activePage={activePage}
            itemsCountPerPage={itemsPerPage}
            totalItemsCount={posts?.length}
            pageRangeDisplayed={10}
            onChange={(pageNumber) => setActivePage(pageNumber)}
            firstPageText={<img alt="left" src={ICON_PAGINATION_LEFT} className="mt-[3px] h-[15px] w-[16px]" />}
            prevPageText={<img alt="left" src={ICON_CHEVRON_LEFT_BLACK} className="mt-[6px] h-[9px] w-[10px]" />}
            lastPageText={<img alt="left" src={ICON_PAGINATION_RIGHT} className="mt-[3px] h-[15px] w-[16px]" />}
            nextPageText={<img alt="left" src={ICON_CHEVRON_RIGHT_BLACK} className="mt-[6px] h-[9px] w-[10px]" />}
          />
        </div>
      </div>
    </>
  )
}

const BlogComponent = ({ isMobile, menuId, subMenuId }: IBlogComponent) => {
  const { t } = useTranslation()
  const { query } = useRouter()

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useBlogPostInfiniteQuery({
    offset: isMobile ? 7 : 9,
    params: {
      selectedMenuId: menuId,
      selectedSubMenuId: subMenuId,
      lang: query.lang,
    },
  })
  const blogPosts = data?.pages.flat() || []

  const handleButtonClick = useCallback(() => {
    fetchNextPage()
  }, [])

  return (
    <>
      <div className={style(isMobile).contentsListBodySectionWrapper}>
        {blogPosts?.map((postItem, i) => {
          return (
            <div key={i}>
              <BlogCard
                isMobile={isMobile}
                title={postItem.title}
                subtitle={postItem.subtitle}
                thumbnail={postItem.thumbnail}
                slug={postItem.slug?.current}
                tagLists={postItem.tagLists}
                category={postItem.category}
                subCategory={postItem.subCategory}
              />
            </div>
          )
        })}
      </div>

      {hasNextPage && (
        <div className={style(isMobile).seeMoreButtonLayout}>
          <DSButton
            size={isMobile ? BUTTON_SIZE.MD : BUTTON_SIZE.LG}
            onClick={handleButtonClick}
            type={BUTTON_TYPE.OUTLINE}
            isLoading={isFetchingNextPage}
          >
            <DSButton.Label>{t(s_blog.category.see_more)}</DSButton.Label>
          </DSButton>
        </div>
      )}
    </>
  )
}

const BlogPostListSection = ({ menuId, subMenuId, isMobile, menuForm }: IBlogPostListSection) => {
  switch (menuForm) {
    case MENU_ID.NOTICE:
      return (
        <div className={style(isMobile).contentsListBodySectionLayout}>
          <NoticeComponent isMobile={isMobile} />
        </div>
      )
    case MENU_ID.FAQ:
      return (
        <div className={style(isMobile).contentsListBodySectionLayout}>
          <FAQSection subMenuId={subMenuId} />
        </div>
      )

    default:
      return (
        <div className={style(isMobile).contentsListBodySectionLayout}>
          <BlogComponent isMobile={isMobile} menuId={menuId} subMenuId={subMenuId} />
        </div>
      )
  }
}
export default BlogPostListSection

const style = (isMobile) =>
  isMobile
    ? {
        noticeLayout: `flex max-w-[688px]  flex-col`,
        noticeElement: `py-[12px]`,
        faqLayout: `flex w-full max-w-[688px] flex-col justify-center gap-[8px]`,
        faqElement: `px-[16px] py-[12px] text-gray-900 hover:rounded-[10px] hover:bg-gray-50 hover:text-purple-600`,
        contentsListBodySectionLayout: `flex h-full w-full flex-col justify-center px-[16px]`,
        contentsListBodySectionWrapper: `mx-auto flex flex-col justify-center gap-y-[12px]`,
        seeMoreButtonLayout: `mx-auto mt-[48px] mb-[40px] w-full`,
      }
    : {
        noticeLayout: `flex max-w-[688px] flex-col`,
        noticeElement: `border-b-1 py-[12px]`,
        faqLayout: `flex w-full max-w-[688px] flex-col justify-center gap-[16px]`,
        faqElement: `px-[16px] py-[12px] text-gray-900 hover:rounded-[10px] hover:bg-gray-50 hover:text-purple-600`,
        contentsListBodySectionLayout: ``,
        contentsListBodySectionWrapper: `grid grid-cols-3 gap-x-[16px] gap-y-[60px]`,
        seeMoreButtonLayout: `mx-auto mt-[48px] w-[160px]`,
      }
